<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'

const { t } = useI18n()

const props = defineProps<{
  invitation: {
    company_name: string | null
    email: string
    invited_by_name: string | null
    account_email: string
  }
  acceptUrl: string
  cancelUrl: string
}>()

const loading = ref(false)

const acceptInvitation = () => {
  router.post(
    props.acceptUrl,
    { confirm: true },
    {
      preserveScroll: true,
      onStart: () => {
        loading.value = true
      },
      onFinish: () => {
        loading.value = false
      },
    },
  )
}

const cancelInvitation = () => {
  router.post(props.cancelUrl, {}, { preserveScroll: true })
}
</script>

<template>
  <Head :title="t('title')" />

  <AuthCard>
    <template #title>{{ t('title') }}</template>
    <template #subtitle>{{ t('subtitle') }}</template>
    <template #content>
      <Fieldset :legend="t('invitationDetails')">
        <div class="flex flex-col gap-1">
          <p>{{ t('company', { company: invitation.company_name ?? t('unknownCompany') }) }}</p>
          <p>{{ t('invitedEmail', { email: invitation.email }) }}</p>
          <p>{{ t('activeAccount', { email: invitation.account_email }) }}</p>
          <p v-if="invitation.invited_by_name">
            {{ t('invitedBy', { name: invitation.invited_by_name }) }}
          </p>
        </div>
      </Fieldset>
    </template>
    <template #footer>
      <div class="mt-6 flex justify-end gap-2">
        <Button :label="t('cancel')" severity="secondary" outlined @click="cancelInvitation" />
        <Button :label="t('accept')" :loading @click="acceptInvitation" />
      </div>
    </template>
  </AuthCard>
</template>

<i18n>
{
  "de": {
    "title": "Unternehmenseinladung bestätigen",
    "subtitle": "Bitte bestätige aktiv, dass du die Einladung mit diesem Account annehmen möchtest.",
    "company": "Unternehmen: {company}",
    "unknownCompany": "Unbekannt",
    "invitationDetails": "Einladungsdetails",
    "invitedEmail": "Eingeladen über E-Mail: {email}",
    "activeAccount": "Aktiver Account: {email}",
    "invitedBy": "Einladung von: {name}",
    "cancel": "Abbrechen",
    "accept": "Einladung annehmen"
  },
  "en": {
    "title": "Confirm company invitation",
    "subtitle": "Please actively confirm that you want to accept this invitation with the current account.",
    "company": "Company: {company}",
    "unknownCompany": "Unknown",
    "invitationDetails": "Invitation details",
    "invitedEmail": "Invited via email: {email}",
    "activeAccount": "Active account: {email}",
    "invitedBy": "Invited by: {name}",
    "cancel": "Cancel",
    "accept": "Accept invitation"
  }
}
</i18n>
